import { call, put, all, takeLatest, select } from 'redux-saga/effects';
import {
  articlesLoaded,
  articlesLoadFailed,
  getArticles as GET_ARTICLES,
  getPetWisdom as GET_PET_WISDOM,
  getContactBrandDataLoaded,
  getPetWisdomAttempt,
  petWisdomFailed,
  petWisdomLoaded,
} from '../actions';
import {
  apiCallWithApiKey,
  get,
} from '../services/api';
import {
  getContentfulEntries as getContentfulEntriesApi,
} from '../services/contentfulApi';
import { useSocialV1T169153ForSagas } from '../services/featureFlagsForSagas';
import { objectToPascalCase } from '../services/utils';
import { COMPANY_CONTACT_US_CONTENT_TYPE } from '../services/contentful.constants';
import { assignContactUsCompanyIds } from '../components/sideMenu/contactUs/contactUs.utils';

function* getArticles(dispatch, { payload }) {
  const { published, skip } = payload;
  const response = yield call(
    get,
    dispatch,
    `api/contentful/GetBlogContent/${published}/${skip}`,
  );

  if (response.IsValid) {
    yield put(articlesLoaded(response.Data));
  } else {
    yield put(articlesLoadFailed());
  }
}

export function* getPetWisdom(dispatch, { payload }) {
  yield put(getPetWisdomAttempt());
  const { published } = payload;
  const response = yield call(
    get,
    dispatch,
    `api/contentful/GetPetWisdom/${published}`,
  );

  if (response.IsValid && response.Data) {
    yield put(petWisdomLoaded(response.Data));
  } else {
    yield put(petWisdomFailed());
  }
}

// Contentful has only company name, no company id so the response needs
// to be mapped to the company id using the CONTACT_US_PROVIDERS constant
function* fetchContactUsInformationEntries() {
  const response = yield call(
    getContentfulEntriesApi,
    { contentType: COMPANY_CONTACT_US_CONTENT_TYPE },
  );

  const items = response.ok ? response.data?.items : null;

  if (!items) {
    return [];
  }

  const formattedItems = items.map((item) => objectToPascalCase(item.fields));

  return assignContactUsCompanyIds(formattedItems);
}

function* getContactDataFromContentful({ brandInformation, companyId }) {
  const entries = yield call(fetchContactUsInformationEntries);
  const entry = entries.find((item) => item.companyId === companyId);

  if (entry) {
    brandInformation.push(entry);
  }
}

function* getContactData({ dispatch, brandInformation, companyId }) {
  const isSocialApi = yield useSocialV1T169153ForSagas();

  const api = isSocialApi ? 'social' : 'api';
  const url = `${api}/Contentful/GetCompanyContactUs`;
  const isPublished = process.env.REACT_APP_NAME === 'Prod';
  const params = { companyId, getPublished: isPublished };

  const response = yield call(get, dispatch, url, params);

  if (response && response.IsValid && response.Data && brandInformation) {
    brandInformation.push({
      ...response.Data,
      companyId,
    });
  } else {
    yield call(getContactDataFromContentful, { brandInformation, companyId });
  }
}

function* getAllContactDataFromContentful({ brandInformation }) {
  const entries = yield call(fetchContactUsInformationEntries);

  brandInformation.push(...entries);
}

export function* getContactBrandData(dispatch) {
  const brandInformation = [];
  let isAllCompaniesRequest = false;
  const store = yield select(({
    personalInformation,
  }) => ({ personalInformation }));

  const insuranceCompanies = store.personalInformation.userInfo.Companies;
  if (insuranceCompanies && insuranceCompanies.length > 0) {
    const companiesDistinct = Array.from(
      new Set(insuranceCompanies.map(({ Id }) => Id)),
    );

    yield all(companiesDistinct.map((companyId) => call(getContactData, {
      brandInformation,
      companyId,
      dispatch,
    })));
  } else {
    const isPublished = process.env.REACT_APP_NAME === 'Prod';
    const path = 'social/Contentful/GetAllCompanyContactUs';
    const url = `${path}?getPublished=${isPublished}`;
    isAllCompaniesRequest = true;

    const response = yield call(
      apiCallWithApiKey,
      url,
      {},
      dispatch,
    );

    if (response.success && response.data && response.data.length > 0) {
      const formattedResponse = objectToPascalCase(response.data);
      const mapped = assignContactUsCompanyIds(formattedResponse);

      brandInformation.push(...mapped);
    } else {
      yield call(getAllContactDataFromContentful, { brandInformation });
    }
  }

  yield put(getContactBrandDataLoaded({
    brandInformation,
    isAllCompaniesRequest,
  }));
}

export function* homeSaga(store) {
  yield all([
    takeLatest(GET_ARTICLES, getArticles, store.dispatch),
    takeLatest(GET_PET_WISDOM, getPetWisdom, store.dispatch),
  ]);
}
